import React, { createContext, useCallback, useContext, useMemo } from "react";
import { getVal, setVal } from "oolib";
import { nanoid } from "nanoid";
import { useQueryClient } from "react-query";

import { useGetAllOptionsForDimension1 } from "../../WidgetBarChart/utils/useGetAllOptionsForDimension1";
import { useGetAllOptionsForDimension2 } from "../../WidgetBarChart/utils/useGetAllOptionsForDimension2";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { DATA_KEYS } from "../DATA_KEYS";
import { getWidgetContentTypes, pruneStaleWidgetFilters } from "../../utils/getWidgetContentTypes";

const SankeyConfigContext = createContext(null);

export const useSankeyConfig = () => {
  const ctx = useContext(SankeyConfigContext);
  if (!ctx) throw new Error("useSankeyConfig must be used within SankeyConfigProvider");
  return ctx;
};

export const SankeyConfigProvider = ({ thisWidgetConfig, setConfig, underEditWidgetIdx, children }) => {

  const queryClient = useQueryClient();
  const allTpls = queryClient.getQueryData("allTpls");
  const { SELF_MANAGED_BASE_CONFIGS, annotationTagsCount } = useGetQueryData("platformConfigs");

  const updateWidget = useCallback((updaterFn) => {
    setConfig((prev) => {
      let widgetConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      const widgetType = widgetConfig?.comp || 'WidgetSankeyChart';
      const prevContentTypes = getWidgetContentTypes(widgetConfig, widgetType);
      widgetConfig = updaterFn(widgetConfig);
      // A write that changes which datasets the widget reads drops the widget
      // filters that belonged to a dataset it left (see pruneStaleWidgetFilters).
      widgetConfig = pruneStaleWidgetFilters(widgetConfig, prevContentTypes, widgetType);
      const fullConfig = JSON.parse(JSON.stringify(prev));
      fullConfig[underEditWidgetIdx] = widgetConfig;
      return fullConfig;
    });
  }, [setConfig, underEditWidgetIdx]);

  // --- dimensions array helpers ---

  const dimensions = getVal(thisWidgetConfig, DATA_KEYS.dimensions) || [];
  // Only treat dim[0] as "selected" if it has actual content (not just a skeleton { _id })
  const firstDim = (dimensions[0]?.display || dimensions[0]?.valuePath) ? dimensions[0] : null;

  // Options: dim[0] gets all options, dim[1+] get options based on dim[0]
  const { options: dim1Options } = useGetAllOptionsForDimension1({ showNumberInputsInOptions: true });

  const subsequentDimOptions = useGetAllOptionsForDimension2({ dimension1Value: firstDim });

  // --- auto title ---

  const buildAutoTitle = useCallback((dims) => {
    return dims
      .map(d => d?.display)
      .filter(Boolean)
      .join(" → ");
  }, []);

  const maybeSetAutoTitle = useCallback((w, dims) => {
    const hasCustomTitle = getVal(w, DATA_KEYS.hasCustomTitle);
    if (!hasCustomTitle) {
      w = setVal(w, DATA_KEYS.title, buildAutoTitle(dims));
    }
    return w;
  }, [buildAutoTitle]);

  // --- handlers ---

  const handleDimChange = useCallback((index, value) => {
    updateWidget(w => {
      const dims = [...(getVal(w, DATA_KEYS.dimensions) || [])];
      // preserve _id
      dims[index] = { _id: dims[index]?._id || nanoid(6), ...value };

      // If first dimension changed, clear all subsequent dimensions (they depend on dim[0])
      if (index === 0) {
        for (let i = 1; i < dims.length; i++) {
          dims[i] = { _id: dims[i]._id };
        }
      }

      w = setVal(w, DATA_KEYS.dimensions, dims);
      w = maybeSetAutoTitle(w, dims);
      return w;
    });
  }, [updateWidget, maybeSetAutoTitle]);

  const handleAddDimension = useCallback(() => {
    updateWidget(w => {
      const dims = [...(getVal(w, DATA_KEYS.dimensions) || [])];
      dims.push({ _id: nanoid(6) });
      w = setVal(w, DATA_KEYS.dimensions, dims);
      return w;
    });
  }, [updateWidget]);

  const handleRemoveDimension = useCallback((index) => {
    updateWidget(w => {
      const dims = [...(getVal(w, DATA_KEYS.dimensions) || [])];
      dims.splice(index, 1);
      // If dim[0] was removed, subsequent dims were selected based on the old dim[0]
      // and may be invalid — clear them
      if (index === 0 && dims.length > 0) {
        for (let i = 0; i < dims.length; i++) {
          dims[i] = { _id: dims[i]._id };
        }
      }
      w = setVal(w, DATA_KEYS.dimensions, dims.length > 0 ? dims : undefined);
      w = maybeSetAutoTitle(w, dims);
      return w;
    });
  }, [updateWidget, maybeSetAutoTitle]);

  const handleReorderDimensions = useCallback((newAry) => {
    updateWidget(w => {
      const oldFirstId = (getVal(w, DATA_KEYS.dimensions) || [])[0]?._id;
      const newFirstId = newAry[0]?._id;
      let dims = [...newAry];
      // If a different item landed at index 0, clear subsequent dims
      if (oldFirstId !== newFirstId && dims.length > 1) {
        for (let i = 1; i < dims.length; i++) {
          dims[i] = { _id: dims[i]._id };
        }
      }
      w = setVal(w, DATA_KEYS.dimensions, dims);
      w = maybeSetAutoTitle(w, dims);
      return w;
    });
  }, [updateWidget, maybeSetAutoTitle]);

  const value = useMemo(() => ({
    widgetConfig: thisWidgetConfig,
    updateWidget,
    dataKeys: DATA_KEYS,
    dimensions,
    firstDim,
    dim1Options,
    subsequentDimOptions,
    handleDimChange,
    handleAddDimension,
    handleRemoveDimension,
    handleReorderDimensions,
    setConfig,
    underEditWidgetIdx,
    allTpls,
    SELF_MANAGED_BASE_CONFIGS,
    annotationTagsCount,
  }), [
    thisWidgetConfig, updateWidget,
    dimensions, firstDim, dim1Options, subsequentDimOptions,
    handleDimChange, handleAddDimension, handleRemoveDimension, handleReorderDimensions,
    setConfig, underEditWidgetIdx, allTpls,
    SELF_MANAGED_BASE_CONFIGS, annotationTagsCount,
  ]);

  return (
    <SankeyConfigContext.Provider value={value}>
      {children}
    </SankeyConfigContext.Provider>
  );
};
